import EmailTagButtons from 'features/EmailEditor/EmailTagButtons'
import {
  CARET_ANCHOR,
  findPlaceholderTag,
  serializeTaggedField,
  splitPlaceholders
} from 'features/EmailEditor/taggedTextHelpers'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'

import styles from './TaggedTextInput.module.scss'

function buildChip(placeholder, label) {
  const chip = document.createElement('span')
  chip.className = styles.chip
  chip.contentEditable = 'false'
  chip.setAttribute('data-placeholder', placeholder)
  chip.title = placeholder
  chip.textContent = label
  return chip
}

function renderValue(element, value) {
  element.innerHTML = ''

  splitPlaceholders(value).forEach(segment => {
    element.appendChild(
      segment.type === 'tag'
        ? buildChip(segment.value, segment.label)
        : document.createTextNode(segment.value)
    )
  })
}

function selectEndOf(element) {
  const range = document.createRange()
  range.selectNodeContents(element)
  range.collapse(false)

  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)

  return range
}

/**
 * Single line template field that shows known {{placeholders}} as chips while
 * still reading and writing the plain text the delivery service expects.
 */
const TaggedTextInput = ({ label, value, onChange, multiline = false }) => {
  const fieldRef = useRef(null)
  const lastEmittedRef = useRef(null)

  useEffect(() => {
    const element = fieldRef.current
    const nextValue = value || ''

    // Rewriting the DOM on every keystroke would drop the caret, so only sync
    // when the change came from outside this field.
    if (!element || nextValue === lastEmittedRef.current) {
      return
    }

    renderValue(element, nextValue)
    lastEmittedRef.current = nextValue
  }, [value])

  const emitChange = () => {
    const nextValue = serializeTaggedField(fieldRef.current)
    lastEmittedRef.current = nextValue
    onChange(nextValue)
  }

  const insertAtCaret = nodes => {
    const element = fieldRef.current
    element.focus()

    const selection = window.getSelection()
    const hasCaretInside =
      selection.rangeCount > 0 && element.contains(selection.getRangeAt(0).startContainer)
    const range = hasCaretInside ? selection.getRangeAt(0) : selectEndOf(element)

    range.deleteContents()

    nodes.forEach(node => {
      range.insertNode(node)
      range.setStartAfter(node)
      range.collapse(true)
    })

    selection.removeAllRanges()
    selection.addRange(range)

    emitChange()
  }

  const handleInsertPlaceholder = placeholder => {
    const tag = findPlaceholderTag(placeholder)
    if (!tag) {
      return
    }

    // The anchor gives the caret somewhere to sit when the chip lands last.
    insertAtCaret([
      buildChip(tag.placeholder, tag.label),
      document.createTextNode(CARET_ANCHOR)
    ])
  }

  const handleKeyDown = event => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()

    if (multiline) {
      insertAtCaret([document.createTextNode('\n')])
    }
  }

  const handlePaste = event => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text/plain')

    insertAtCaret(
      splitPlaceholders(pasted).map(segment =>
        segment.type === 'tag'
          ? buildChip(segment.value, segment.label)
          : document.createTextNode(segment.value)
      )
    )
  }

  return (
    <div className={styles.container}>
      {label && <span className={styles.label}>{label}</span>}
      <div
        ref={fieldRef}
        className={[styles.field, multiline && styles.fieldMultiline].filter(Boolean).join(' ')}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={label}
        aria-multiline={multiline}
        onInput={emitChange}
        onBlur={emitChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
      <EmailTagButtons onInsert={handleInsertPlaceholder} />
    </div>
  )
}

TaggedTextInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  multiline: PropTypes.bool
}

export default TaggedTextInput
